import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, ImagePlus } from "lucide-react";
import { api, errText, photoUrl } from "../../lib/api";
import { compressImage } from "../../lib/image";
import { useLanguage } from "../../context/LanguageContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { BabyCard } from "../BabyCard";

const EMPTY = { name: "", sex: "unknown", color: "", notes: "", photo: "", status: "available" };

const Pick = ({ value, onChange, opts, t, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="rounded-xl h-9" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent className="bg-white">{opts.map((o) => <SelectItem key={o} value={o} data-testid={`${testid}-${o}`}>{t(`bb_${o}`)}</SelectItem>)}</SelectContent>
  </Select>
);

const BabyForm = ({ initial, onSave, onCancel, t }) => {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const onFile = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", await compressImage(file));
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo")(data.path);
    } catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-purple-200 bg-purple-50/40 p-3 space-y-2" data-testid="baby-form">
      <div className="flex gap-3">
        <label className="h-20 w-20 shrink-0 rounded-lg border-2 border-dashed border-slate-300 hover:border-[#F5551E] flex items-center justify-center cursor-pointer overflow-hidden bg-white" data-testid="baby-photo-upload">
          {busy ? <Loader2 size={16} className="animate-spin text-[#F5551E]" /> : f.photo ? <img src={photoUrl(f.photo)} alt="" className="h-full w-full object-cover" /> : <ImagePlus size={18} className="text-slate-400" />}
          <input type="file" accept="image/*" className="hidden" onChange={onFile} />
        </label>
        <div className="flex-1 grid grid-cols-2 gap-2">
          <Input value={f.name} onChange={(e) => set("name")(e.target.value)} maxLength={40} placeholder={t("bb_name")} data-testid="baby-name" className="rounded-xl h-9 col-span-2" />
          <Pick value={f.sex} onChange={set("sex")} opts={["male", "female", "unknown"]} t={t} testid="baby-sex" />
          <Pick value={f.status} onChange={set("status")} opts={["available", "reserved", "home"]} t={t} testid="baby-status" />
        </div>
      </div>
      <Input value={f.color} onChange={(e) => set("color")(e.target.value)} maxLength={60} placeholder={t("bb_color")} data-testid="baby-color" className="rounded-xl h-9" />
      <Input value={f.notes} onChange={(e) => set("notes")(e.target.value)} maxLength={300} placeholder={t("bb_notes")} data-testid="baby-notes" className="rounded-xl h-9" />
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} data-testid="baby-cancel" className="rounded-lg">{t("bb_cancel")}</Button>
        <Button size="sm" onClick={() => onSave(f)} disabled={busy} data-testid="baby-save" className="rounded-lg bg-[#F5551E] hover:bg-[#E04812] text-white">{t("bb_save")}</Button>
      </div>
    </div>
  );
};

export function BabiesManager({ litter, onClose }) {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const load = () => litter && api.get(`/litters/${litter.id}/babies`).then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { setEditing(null); load(); }, [litter]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (f) => {
    const body = { name: f.name, sex: f.sex, color: f.color, notes: f.notes, photo: f.photo, status: f.status };
    try {
      if (f.id) await api.put(`/my/litters/${litter.id}/babies/${f.id}`, body);
      else await api.post(`/my/litters/${litter.id}/babies`, body);
      setEditing(null); load();
    } catch (e) { toast.error(errText(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/my/litters/${litter.id}/babies/${id}`); load(); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <Dialog open={!!litter} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-white" data-testid="babies-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#12234A]">{t("bb_title")}</DialogTitle>
          <DialogDescription className="text-slate-500">{t("bb_desc")}</DialogDescription>
        </DialogHeader>
        {editing ? <BabyForm initial={editing} onSave={save} onCancel={() => setEditing(null)} t={t} /> : (
          <Button onClick={() => setEditing(EMPTY)} data-testid="baby-add" className="self-start rounded-xl bg-purple-600 hover:bg-purple-700 text-white gap-1.5"><Plus size={15} /> {t("bb_add")}</Button>
        )}
        {items.length === 0 ? <p className="text-xs text-slate-400">{t("bb_none")}</p> : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {items.map((b) => (
              <BabyCard key={b.id} b={b} t={t} action={
                <span className="flex gap-1">
                  <button onClick={() => setEditing(b)} data-testid={`baby-edit-${b.id}`} className="h-6 w-6 rounded-full bg-white/90 text-slate-600 flex items-center justify-center"><Pencil size={11} /></button>
                  {confirmId === b.id
                    ? <button onClick={() => { setConfirmId(null); del(b.id); }} data-testid={`baby-delete-confirm-${b.id}`} className="h-6 px-2 rounded-full bg-red-500 text-white text-[10px] font-bold">{t("bb_del_q")}</button>
                    : <button onClick={() => setConfirmId(b.id)} data-testid={`baby-delete-${b.id}`} className="h-6 w-6 rounded-full bg-white/90 text-red-500 flex items-center justify-center"><Trash2 size={11} /></button>}
                </span>} />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
